import _ from 'lodash';

/**
 * This regex matches any of .?! when they are followed by either whitespace or </p>
 */
const SENTENCE_PUNCTUATION = /[.?!](?:\s|(?:<\/p>)h)?/g;

/** RAW STRING CONTENT */

/**
 * Get the number of full sentences in the string.
 * !!!Does not account for trailing sentence fragments.
 * @private
 * @param {String} content
 * @returns {Number} - The number of full sentences in the content.
 */
const getNumberOfFullSentences = (content) =>
  content.match(SENTENCE_PUNCTUATION)?.length || 0;

/**
 * Generate a string with only up to the number of sentences limited.
 * @private
 * @param {String} content
 * @param {Number} numberOfSentences
 * @returns {String}
 */
export const generateStringAbstract = (content, numberOfSentences = 0) => {
  // No delimiters means no full sentences, return the sentence fragment.
  const delimiters = content.match(SENTENCE_PUNCTUATION);
  if (numberOfSentences === 0 || !delimiters) {
    return content;
  }

  // Process the paragraph to extract the right number of sentences to show before the "show more"
  const outputString = _.reduce(
    content.split(SENTENCE_PUNCTUATION).slice(0, numberOfSentences),
    (string, sentence, i) => string.concat(sentence, delimiters[i] ?? ''),
    ''
  );

  return outputString;
};

/**
 * Determine if a string is eligible to expand based on the number of sentences requested.
 * @private
 * @param {String} content
 * @param {Number} numberOfSentences
 * @returns {Boolean}
 */
export const isStringEligibleToExpand = (content, numberOfSentences = 0) => {
  if (numberOfSentences === 0) {
    return false;
  }

  return getNumberOfFullSentences(content) > numberOfSentences;
};

/** STRUCTURED CONTENT */

/**
 * Determine if the content is valid structured content using some generic assumptions.
 * @param {Object} content
 * @returns {Boolean}
 */
export const isStructuredContent = (content) =>
  content?.type && content?.version && content?.props;

/**
 * Generate a structured content doc with only up to the number of sentences limited.
 * @private
 * @param {Object} initialContent
 * @param {Number} numberOfSentences
 * @returns {Object} - The Structured Content abstract and number of sentences up to the limit.
 */
export const generateStructuredContentAbstract = (
  initialContent,
  numberOfSentences = 0
) => {
  let sentenceCount = 0;

  // Recursive helper to generate an abstract of a SC doc.
  const generateAbstract = (content) => {
    if (sentenceCount >= numberOfSentences || !content) {
      return null;
    }

    if (_.isString(content)) {
      const numberOfFullSentences =
        content.match(SENTENCE_PUNCTUATION)?.length ?? 0;
      // If the content isn't a full sentence, then don't count against the sentence counter and return as is.
      if (numberOfFullSentences === 0) {
        return content;
      }

      // Ensure the number of sentences doesn't exceed the limit.
      const limit = Math.min(
        numberOfFullSentences,
        numberOfSentences - sentenceCount
      );
      sentenceCount += limit;

      /**
       * Include punctuation by using regular string abstract generation,
       * while preserving trailing sentence fragments for inline treatments like
       * links, bold or italics.
       */
      const hasTrailingFragment =
        !!content.split(SENTENCE_PUNCTUATION)[numberOfFullSentences];
      const abstract = generateStringAbstract(
        content,
        limit + Number(hasTrailingFragment)
      );
      return abstract;
    }
    if (_.isArray(content)) {
      // Filter out docs that may have returned null because they exceeded the counter.
      return content.map(generateAbstract).filter(Boolean);
    }
    if (_.isObject(content)) {
      const {
        type,
        props: { children, ...otherProps },
        version,
      } = content;

      // Reconstruct the document, cognizant of limit set by the counter
      return {
        type,
        version,
        props: {
          children: generateAbstract(children),
          ...otherProps,
        },
      };
    }

    // We should never get here with a valid document
    throw new Error(
      `Ill formatted StructuredContent document: ${JSON.stringify(
        initialContent
      )}`
    );
  };

  return generateAbstract(initialContent);
};

/**
 * Determine if a structured content document is eligible to expand.
 * @private
 * @param {Object} initialContent
 * @param {Number} numberOfSentences
 * @returns {Boolean}
 */
export const isStructuredContentEligibleToExpand = (
  initialContent,
  numberOfSentences
) => {
  if (numberOfSentences === 0) {
    return false;
  }

  // Recursive helper to count the number of sentences in a SC doc.
  const countSentences = (content) => {
    if (!content) {
      return 0;
    }
    if (_.isString(content)) {
      return getNumberOfFullSentences(content);
    }
    if (_.isArray(content)) {
      return _.reduce(
        content,
        (total, contentBlob) => total + countSentences(contentBlob),
        0
      );
    }
    if (_.isObject(content)) {
      return countSentences(content.props.children);
    }

    // We should never get here with a valid document
    throw new Error(
      `Ill formatted StructuredContent document: ${JSON.stringify(
        initialContent
      )}`
    );
  };

  return countSentences(initialContent) > numberOfSentences;
};

/** GENERIC CONTENT */

/**
 * Construct an "abstract" of provided content by extracting the requested number of sentences.
 * @param {Object|String} content
 * @param {Number} numberOfSentences
 * @returns {Object|String} - The abstract.
 */
export const generateAbstract = (content, numberOfSentences) =>
  isStructuredContent(content)
    ? generateStructuredContentAbstract(content, numberOfSentences)
    : generateStringAbstract(content, numberOfSentences);

/**
 * Determine whether the content is eligible to expand based on the requested number of sentences.
 * @param {Object|String}
 * @param {Number}
 * @returns {Boolean}
 */
export const isEligibleToExpand = (content, numberOfSentences) =>
  isStructuredContent(content)
    ? isStructuredContentEligibleToExpand(content, numberOfSentences)
    : isStringEligibleToExpand(content, numberOfSentences);
